import React, { useRef, useEffect, useMemo, memo } from 'react';
import { Box } from '@mui/material';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import type { MapMarker } from './mapTypes';
export type { MapMarker };

interface InlineMapPreviewProps {
  latitude: number;
  longitude: number;
  height: number | string;
  width?: number | string;
  onClick?: () => void;
  children?: React.ReactNode;
  /** Additional markers to show on the map */
  markers?: MapMarker[];
  /** Dim markers when map is collapsed */
  dimMarkers?: boolean;
  /** Distance unit preference for display */
  distanceUnit?: 'MILES' | 'KILOMETERS';
  /** Enable drag to pan and scroll/pinch to zoom */
  interactive?: boolean;
  /** Scale factor for marker icons (default 1). Use < 1 for smaller, subtler markers. */
  markerScale?: number;
}

// Build tile URL through our proxy
// Bump TILE_VERSION when switching tile providers to bust SW + HTTP caches
const TILE_VERSION = 2;
const buildTileUrl = (): string => {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const base = apiUrl
    ? `${apiUrl}/tiles/{z}/{x}/{y}.png`
    : `/api/v1/tiles/{z}/{x}/{y}.png`;
  return `${base}?v=${TILE_VERSION}`;
};

/**
 * Create a Leaflet DivIcon from an SVG string
 */
const makeDivIcon = (svgHtml: string, size: [number, number], anchor: [number, number]) =>
  L.divIcon({
    html: svgHtml,
    className: '',
    iconSize: size,
    iconAnchor: anchor,
  });

// SVG icon builders — white stroke border on all icons for contrast
const flagSvg = (color: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" style="filter:drop-shadow(0 2px 2px rgba(0,0,0,0.3))"><path stroke="white" stroke-width="1.5" stroke-linejoin="round" fill="${color}" d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/></svg>`;

const homeSvg = (color: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" style="filter:drop-shadow(0 2px 2px rgba(0,0,0,0.3))"><path stroke="white" stroke-width="1.5" stroke-linejoin="round" fill="${color}" d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>`;

const fmdGoodSvg = (color: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" style="filter:drop-shadow(0 2px 2px rgba(0,0,0,0.3))"><path stroke="white" stroke-width="1.5" stroke-linejoin="round" fill="${color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7m0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5"/></svg>`;

const placeSvg = (color: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" style="filter:drop-shadow(0 2px 2px rgba(0,0,0,0.3))"><path stroke="white" stroke-width="1.5" stroke-linejoin="round" fill="${color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7m0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5"/></svg>`;

/**
 * Get the resolved CSS color for MUI palette references like "error.main"
 */
const resolveColor = (color?: string): string => {
  if (!color) return '#1976d2';
  if (color.startsWith('#') || color.startsWith('rgb')) return color;
  // MUI palette shortcuts
  const palette: Record<string, string> = {
    'error.main': '#d32f2f',
    'info.main': '#0288d1',
    'primary.main': '#1976d2',
    'success.main': '#2e7d32',
    'warning.main': '#ed6c02',
  };
  return palette[`${color}.main`] || palette[color] || color;
};

const getMarkerSvg = (marker: MapMarker): { svg: string; size: [number, number]; anchor: [number, number] } => {
  const color = resolveColor(marker.color);
  switch (marker.type) {
    case 'home':
      return { svg: homeSvg(color), size: [28, 28], anchor: [14, 14] };
    case 'previous':
      return { svg: fmdGoodSvg(color), size: [28, 28], anchor: [14, 28] };
    default:
      return { svg: placeSvg(color), size: [28, 28], anchor: [14, 28] };
  }
};

/**
 * Calculate zoom level to fit bounds within given pixel dimensions
 */
const getZoomToFitBounds = (
  minLat: number, maxLat: number, minLon: number, maxLon: number,
  width: number, height: number
): number => {
  const WORLD_SIZE = 256;
  const latToY = (lat: number) => {
    const sinLat = Math.sin(lat * Math.PI / 180);
    return (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * WORLD_SIZE;
  };
  const lonToX = (lon: number) => ((lon + 180) / 360) * WORLD_SIZE;

  const x1 = lonToX(minLon);
  const x2 = lonToX(maxLon);
  const y1 = latToY(maxLat);
  const y2 = latToY(minLat);

  const dx = Math.abs(x2 - x1);
  const dy = Math.abs(y2 - y1);

  const padding = 0.2;
  const zoomX = dx > 0 ? Math.log2(width * (1 - padding * 2) / dx) : 20;
  const zoomY = dy > 0 ? Math.log2(height * (1 - padding * 2) / dy) : 20;

  return Math.max(2, Math.min(14, Math.min(zoomX, zoomY)));
};



/**
 * Component to fit bounds when markers change
 */
const FitBounds: React.FC<{
  latitude: number;
  longitude: number;
  markers: MapMarker[];
  interactive: boolean;
}> = ({ latitude, longitude, markers, interactive }) => {
  const map = useMap();
  const prevKeyRef = useRef('');
  const wasInteractiveRef = useRef(false);
  // Store the initial fitted view so we can return to it on close
  const initialViewRef = useRef<{ center: L.LatLng; zoom: number } | null>(null);

  useEffect(() => {
    // When closing the map (interactive → false), snap to the initial fitted
    // view.  The MapDrawer hides the map during this reset (wipe animation)
    // so the user never sees grey tiles from the zoom change.
    if (!interactive && wasInteractiveRef.current) {
      wasInteractiveRef.current = false;
      if (initialViewRef.current) {
        map.setView(initialViewRef.current.center, initialViewRef.current.zoom, { animate: false });
      }
      return;
    }
    wasInteractiveRef.current = interactive;

    const allPoints = [
      { lat: latitude, lon: longitude },
      ...markers.map(m => ({ lat: m.latitude, lon: m.longitude })),
    ];

    if (markers.length > 0) {
      const lats = allPoints.map(p => p.lat);
      const lons = allPoints.map(p => p.lon);
      const bounds = L.latLngBounds(
        [Math.min(...lats), Math.min(...lons)],
        [Math.max(...lats), Math.max(...lons)],
      );
      const key = bounds.toBBoxString();
      if (key !== prevKeyRef.current) {
        prevKeyRef.current = key;
        const container = map.getContainer();
        const w = container.clientWidth;
        const h = container.clientHeight;
        const zoom = getZoomToFitBounds(
          Math.min(...lats), Math.max(...lats),
          Math.min(...lons), Math.max(...lons),
          w || 300, h || 200,
        );
        const center = bounds.getCenter();
        initialViewRef.current = { center, zoom };
        // First render: snap immediately; subsequent: animate
        map.setView(center, zoom, { animate: false });
      }
    } else {
      const key = `${latitude},${longitude}`;
      if (key !== prevKeyRef.current) {
        prevKeyRef.current = key;
        const center = L.latLng(latitude, longitude);
        initialViewRef.current = { center, zoom: 14 };
        map.setView(center, 14, { animate: false });
      }
    }
  }, [map, latitude, longitude, markers, interactive]);

  return null;
};

/**
 * Component to toggle map interactivity
 */
const InteractivityControl: React.FC<{ interactive: boolean }> = ({ interactive }) => {
  const map = useMap();

  useEffect(() => {
    if (interactive) {
      map.dragging.enable();
      map.scrollWheelZoom.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
    } else {
      map.dragging.disable();
      map.scrollWheelZoom.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
    }
  }, [map, interactive]);

  return null;
};

/**
 * Safety guard: ensures _animatingZoom is cleared after any zoom animation.
 *
 * Leaflet's _animateZoom sets _animatingZoom=true and relies on CSS
 * transitionend (or a 250ms setTimeout fallback) to clear it.  If neither
 * fires reliably, the flag stays stuck and blocks ALL tile loading via
 * _onMoveEnd.  This affects native pinch zoom and any other code path
 * that calls _animateZoom.
 *
 * This component watches for zoomanim events and forces _onZoomTransitionEnd
 * after 350ms if _animatingZoom is still true.
 */
const ZoomEndGuard: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const guard = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if ((map as any)._animatingZoom) {
          (map as any)._onZoomTransitionEnd();
        }
      }, 350);
    };
    map.on('zoomanim', guard);
    return () => {
      clearTimeout(timer);
      map.off('zoomanim', guard);
    };
  }, [map]);
  return null;
};

/**
 * Enables Google Maps-style double-tap-drag zoom on touch devices.
 * - Double-tap and release: zoom in one level (like standard double-tap zoom)
 * - Double-tap and hold, then drag down/up: continuous zoom in/out
 */
const DoubleTapDragZoom: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    let lastTapTime = 0;
    let dragZoomActive = false;
    let didDrag = false;
    let initialY = 0;
    let initialZoom = 0;
    let currentZoom = 0;
    // Cooldown after drag-zoom ends to prevent triple-tap re-triggering
    let lastGestureEnd = 0;
    // Map center captured at gesture start — drag-zoom stays centered here
    let initialCenter: L.LatLng | null = null;
    // Tap location in lat/lng — double-tap zoom targets this point
    let tapLatLng: L.LatLng | null = null;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const now = Date.now();
      const delta = now - lastTapTime;

      if (delta < 300 && delta > 0 && now - lastGestureEnd > 400) {
        // Second tap — enter drag-zoom mode (may become a plain double-tap zoom)
        dragZoomActive = true;
        didDrag = false;
        initialY = e.touches[0].clientY;
        initialZoom = map.getZoom();
        currentZoom = initialZoom;
        initialCenter = map.getCenter();
        // Convert tap pixel position to lat/lng for double-tap zoom target
        const rect = container.getBoundingClientRect();
        const point = L.point(
          e.touches[0].clientX - rect.left,
          e.touches[0].clientY - rect.top,
        );
        tapLatLng = map.containerPointToLatLng(point);
        map.doubleClickZoom.disable();
        map.dragging.disable();
        e.preventDefault();
      }
      lastTapTime = now;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!dragZoomActive || e.touches.length !== 1) return;
      // Drag down = zoom in, drag up = zoom out
      const deltaY = e.touches[0].clientY - initialY;
      // Only enter drag-zoom after meaningful movement (10px threshold)
      if (!didDrag && Math.abs(deltaY) < 10) return;
      didDrag = true;
      e.preventDefault();
      const newZoom = initialZoom + deltaY / 80;
      currentZoom = Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), newZoom));
      // Keep the map center fixed while zooming — use _move with
      // {pinch: true} so GridLayer skips tile loading (noPrune + noUpdate),
      // keeping existing tiles CSS-scaled during the gesture.
      (map as any)._move(initialCenter!, currentZoom, { pinch: true, round: false });
    };

    const onTouchEnd = () => {
      if (dragZoomActive) {
        dragZoomActive = false;
        lastGestureEnd = Date.now();
        if (didDrag) {
          // Finalize the drag-zoom: fire moveend to trigger tile loading.
          // We deliberately avoid _animateZoom (which sets _animatingZoom=true
          // and relies on CSS transitionend / 250ms timeout to clear it —
          // if that fails, _animatingZoom stays stuck and blocks ALL future
          // tile loading).  The map state is already correct from _move calls
          // during the drag; old tile levels remain as CSS-scaled fallback
          // while new tiles load — no grey flash.
          map.fire('moveend');
        } else {
          // No drag — plain double-tap: zoom in one level centered on tap location
          const newZoom = Math.min(map.getMaxZoom(), Math.round(initialZoom) + 1);
          map.setView(tapLatLng || map.getCenter(), newZoom, { animate: true });
        }
        map.doubleClickZoom.enable();
        map.dragging.enable();
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, [map]);

  return null;
};

/**
 * Watches the map container for size changes and calls invalidateSize()
 */
const InvalidateOnResize: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);

  return null;
};

/**
 * A flight line that stays perfectly in sync with markers during zoom.
 *
 * Two invisible Leaflet markers at `from` and `to` are positioned natively
 * by Leaflet through every zoom frame.  A requestAnimationFrame loop reads
 * their actual rendered DOM positions (getBoundingClientRect) and draws an
 * SVG line between them inside a custom pane (z-index 450, between overlay
 * and markers).  The visual positions are converted to the pane's local
 * coordinate space by dividing by the CSS scale on _mapPane.
 */
const FLIGHT_LINE_PANE = 'flightLines';

const FlightLine: React.FC<{
  from: [number, number];
  to: [number, number];
  color: string;
  weight?: number;
  opacity?: number;
}> = ({ from, to, color, weight = 3, opacity = 0.45 }) => {
  const map = useMap();

  useEffect(() => {
    const fromLL = L.latLng(from[0], from[1]);
    const toLL = L.latLng(to[0], to[1]);

    // Custom pane between overlay (400) and markers (600)
    if (!map.getPane(FLIGHT_LINE_PANE)) {
      map.createPane(FLIGHT_LINE_PANE).style.zIndex = '450';
    }
    const pane = map.getPane(FLIGHT_LINE_PANE)!;

    const emptyIcon = L.divIcon({
      html: '',
      className: '',
      iconSize: [0, 0],
      iconAnchor: [0, 0],
    });

    // Invisible anchor markers — Leaflet positions these natively
    const fromMarker = L.marker(fromLL, {
      icon: emptyIcon,
      interactive: false,
    }).addTo(map);

    const toMarker = L.marker(toLL, {
      icon: emptyIcon,
      interactive: false,
    }).addTo(map);

    // SVG inside the custom pane (for correct z-ordering)
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.cssText =
      'position:absolute;top:0;left:0;overflow:visible;pointer-events:none;width:1px;height:1px;';

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('stroke', color);
    line.setAttribute('stroke-width', String(weight));
    line.setAttribute('stroke-opacity', String(opacity));
    line.setAttribute('stroke-linecap', 'round');
    svg.appendChild(line);
    pane.appendChild(svg);

    const mapPane = (map as any)._mapPane as HTMLElement;

    let rafId: number;
    const update = () => {
      const fromEl = fromMarker.getElement();
      const toEl = toMarker.getElement();
      if (fromEl && toEl) {
        // CSS scale applied to _mapPane during zoom animations (1.0 at rest)
        const matrix = new DOMMatrix(getComputedStyle(mapPane).transform);
        const scale = matrix.a || 1;

        // SVG origin in visual (screen) coords
        const sr = svg.getBoundingClientRect();
        const fr = fromEl.getBoundingClientRect();
        const tr = toEl.getBoundingClientRect();

        // Convert visual offsets to local (pre-transform) coordinates
        line.setAttribute('x1', String((fr.left - sr.left) / scale));
        line.setAttribute('y1', String((fr.top - sr.top) / scale));
        line.setAttribute('x2', String((tr.left - sr.left) / scale));
        line.setAttribute('y2', String((tr.top - sr.top) / scale));
      }
      rafId = requestAnimationFrame(update);
    };
    rafId = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(rafId);
      fromMarker.remove();
      toMarker.remove();
      pane.removeChild(svg);
    };
  }, [map, from, to, color, weight, opacity]);

  return null;
};

/**
 * A map preview component using Leaflet for smooth pan/zoom.
 * Supports overlay content and additional markers with travel arcs.
 */
const InlineMapPreview: React.FC<InlineMapPreviewProps> = ({
  latitude,
  longitude,
  height,
  width = '100%',
  onClick,
  children,
  markers = [],
  dimMarkers = false,
  distanceUnit = 'MILES',
  interactive = false,
  markerScale = 1,
}) => {
  const tileUrl = useMemo(buildTileUrl, []);

  const wrapScaled = (svgHtml: string, scale: number) =>
    scale === 1 ? svgHtml : `<div style="transform:scale(${scale});transform-origin:top left">${svgHtml}</div>`;

  // Venue flag icon
  const venueIcon = useMemo(
    () => {
      const baseSize: [number, number] = [32, 32];
      const baseAnchor: [number, number] = [6, 30];
      const s: [number, number] = [Math.round(baseSize[0] * markerScale), Math.round(baseSize[1] * markerScale)];
      const a: [number, number] = [Math.round(baseAnchor[0] * markerScale), Math.round(baseAnchor[1] * markerScale)];
      return makeDivIcon(wrapScaled(flagSvg('#d32f2f'), markerScale), s, a);
    },
    [markerScale],
  );

  // Marker icons
  const markerIcons = useMemo(
    () => markers.map(m => {
      const { svg, size, anchor } = getMarkerSvg(m);
      const s: [number, number] = [Math.round(size[0] * markerScale), Math.round(size[1] * markerScale)];
      const a: [number, number] = [Math.round(anchor[0] * markerScale), Math.round(anchor[1] * markerScale)];
      return makeDivIcon(wrapScaled(svg, markerScale), s, a);
    }),
    [markers, markerScale],
  );

  // Flight lines + distance pills (native Leaflet Polyline + DivIcon)
  const flightLines = useMemo(() => {
    const markersWithDist = markers
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.distanceMeters !== undefined);
    if (markersWithDist.length === 0) return null;

    const hasMultiple = markersWithDist.length > 1;
    const dest: [number, number] = [latitude, longitude];

    // Estimate map span for overlap threshold
    const allLats = [latitude, ...markersWithDist.map(({ m }) => m.latitude)];
    const allLngs = [longitude, ...markersWithDist.map(({ m }) => m.longitude)];
    const mapSpan = Math.max(
      Math.max(...allLats) - Math.min(...allLats),
      Math.max(...allLngs) - Math.min(...allLngs),
    ) || 0.01;
    const minSep = mapSpan * 0.07;
    const tCandidates = [0.5, 0.42, 0.58, 0.34, 0.66, 0.26, 0.74, 0.18, 0.82];
    const placedPositions: [number, number][] = [];

    // Pre-assign left/right sides by sorting markers by angle from venue,
    // then alternating — spatially adjacent lines always get opposite sides
    const sideByMarkerIndex = new Map<number, 'left' | 'right'>();
    if (hasMultiple) {
      const sorted = [...markersWithDist].sort((a, b) => {
        const angleA = Math.atan2(a.m.latitude - latitude, a.m.longitude - longitude);
        const angleB = Math.atan2(b.m.latitude - latitude, b.m.longitude - longitude);
        return angleA - angleB;
      });
      sorted.forEach(({ i }, idx) => {
        sideByMarkerIndex.set(i, idx % 2 === 0 ? 'left' : 'right');
      });
    }

    return markersWithDist.map(({ m, i: markerIndex }) => {
      const from: [number, number] = [m.latitude, m.longitude];
      const lineColor = resolveColor(m.color);

      const distanceMiles = m.distanceMeters! / 1609.34;
      const distanceKm = m.distanceMeters! / 1000;
      const milesStr = distanceMiles >= 10 ? Math.round(distanceMiles).toString() : distanceMiles.toFixed(1);
      const kmStr = distanceKm >= 10 ? Math.round(distanceKm).toString() : distanceKm.toFixed(1);
      const distanceStr = distanceUnit === 'KILOMETERS' ? `${kmStr} km` : `${milesStr} mi`;
      const label = hasMultiple && m.playerName ? `${m.playerName} ${distanceStr}` : distanceStr;

      // Find a position along the line that doesn't overlap with already-placed labels
      let labelPos: [number, number] = [0, 0];
      for (const t of tCandidates) {
        labelPos = [
          from[0] + (dest[0] - from[0]) * t,
          from[1] + (dest[1] - from[1]) * t,
        ];
        const tooClose = placedPositions.some(p =>
          Math.sqrt((labelPos[0] - p[0]) ** 2 + (labelPos[1] - p[1]) ** 2) < minSep,
        );
        if (!tooClose) break;
      }
      placedPositions.push(labelPos);

      const bubbleWidth = Math.max(60, label.length * 7 + 16);
      const bubbleHeight = 22;
      const caretW = 10; // width along box edge
      const caretH = 6;  // how far it sticks out

      // Perpendicular to the line in screen coords (X=lng right, Y=lat down)
      const lineDx = dest[1] - from[1];
      const lineDy = -(dest[0] - from[0]); // screen Y inverted
      const lineLen = Math.sqrt(lineDx * lineDx + lineDy * lineDy) || 1;
      // Perpendicular direction (90° clockwise) = bubble offset direction
      const perpX = lineDy / lineLen;
      const perpY = -lineDx / lineLen;
      // Caret points opposite (back toward line)
      const caretDirX = -perpX;
      const caretDirY = -perpY;

      // Snap caret to a box side — prefer left/right when multiple sources
      // so labels sit beside the line rather than above/below where they
      // collide with converging routes
      let caretSide: 'top' | 'bottom' | 'left' | 'right';
      if (hasMultiple) {
        // Use pre-computed side based on angular sort from venue
        caretSide = sideByMarkerIndex.get(markerIndex) || 'left';
      } else {
        if (Math.abs(caretDirX) > Math.abs(caretDirY)) {
          caretSide = caretDirX > 0 ? 'right' : 'left';
        } else {
          caretSide = caretDirY > 0 ? 'bottom' : 'top';
        }
      }

      // SVG position, shape, and icon anchor per side
      let svgStyle: string;
      let svgW: number, svgH: number;
      let pts: string;
      let anchorX = bubbleWidth / 2;
      let anchorY = bubbleHeight / 2;

      switch (caretSide) {
        case 'bottom':
          svgW = caretW; svgH = caretH;
          svgStyle = `left:${bubbleWidth / 2 - caretW / 2}px;top:${bubbleHeight}px;`;
          pts = `0,0 ${caretW},0 ${caretW / 2},${caretH}`;
          anchorY = bubbleHeight + caretH;
          break;
        case 'top':
          svgW = caretW; svgH = caretH;
          svgStyle = `left:${bubbleWidth / 2 - caretW / 2}px;top:${-caretH}px;`;
          pts = `${caretW / 2},0 0,${caretH} ${caretW},${caretH}`;
          anchorY = -caretH;
          break;
        case 'right':
          svgW = caretH; svgH = caretW;
          svgStyle = `left:${bubbleWidth}px;top:${bubbleHeight / 2 - caretW / 2}px;`;
          pts = `0,0 ${caretH},${caretW / 2} 0,${caretW}`;
          anchorX = bubbleWidth + caretH;
          break;
        case 'left':
          svgW = caretH; svgH = caretW;
          svgStyle = `left:${-caretH}px;top:${bubbleHeight / 2 - caretW / 2}px;`;
          pts = `${caretH},0 0,${caretW / 2} ${caretH},${caretW}`;
          anchorX = -caretH;
          break;
      }

      const pillIcon = L.divIcon({
        html: `<div style="
          position:relative;
          pointer-events:none;
          filter:drop-shadow(0 1px 3px rgba(0,0,0,0.3));
        "><div style="
          background:white;
          border-radius:3px;
          padding:0 8px;
          font-size:11px;
          font-weight:500;
          color:${lineColor};
          font-family:Roboto,sans-serif;
          white-space:nowrap;
          text-align:center;
          height:${bubbleHeight}px;
          line-height:${bubbleHeight}px;
        ">${label}</div><svg style="
          position:absolute;${svgStyle}overflow:visible;
        " width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}">
          <polygon points="${pts}" fill="white"/>
        </svg></div>`,
        className: '',
        iconSize: [bubbleWidth, bubbleHeight],
        iconAnchor: [anchorX, anchorY],
      });

      return { from, dest, lineColor, midpoint: labelPos, pillIcon, key: markerIndex };
    });
  }, [markers, latitude, longitude, distanceUnit]);

  const center: [number, number] = [latitude, longitude];

  return (
    <Box
      onClick={onClick}
      sx={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : undefined,
        // Dim the markers/arcs layer
        '& .leaflet-marker-pane, & .leaflet-overlay-pane': {
          opacity: dimMarkers ? 0.4 : 1,
          transition: 'opacity 0.2s ease',
        },
        // Hide zoom control and attribution for clean look
        '& .leaflet-control-zoom': { display: 'none' },
        '& .leaflet-control-attribution': { display: 'none' },
        // Prevent Leaflet from capturing clicks when non-interactive
        ...(!interactive && {
          '& .leaflet-container': { pointerEvents: 'none' },
        }),
      }}
    >
      <MapContainer
        center={center}
        zoom={14}
        style={{ width: '100%', height: '100%' }}
        zoomControl={false}
        attributionControl={false}
        dragging={interactive}
        scrollWheelZoom={interactive}
        touchZoom={interactive}
        doubleClickZoom={interactive}
        zoomSnap={0}
        zoomDelta={1}
      >
        <TileLayer url={tileUrl} updateWhenIdle={false} />
        <InvalidateOnResize />
        <FitBounds latitude={latitude} longitude={longitude} markers={markers} interactive={interactive} />
        <InteractivityControl interactive={interactive} />
        {interactive && <ZoomEndGuard />}
        {interactive && <DoubleTapDragZoom />}

        {/* Venue flag */}
        <Marker position={[latitude, longitude]} icon={venueIcon} />

        {/* Additional markers */}
        {markers.map((marker, i) => (
          <Marker
            key={`marker-${i}`}
            position={[marker.latitude, marker.longitude]}
            icon={markerIcons[i]}
          />
        ))}

        {/* Flight lines and distance pills — native Leaflet layers */}
        {!dimMarkers && flightLines?.map(({ from, dest, lineColor, midpoint, pillIcon, key }) => (
          <React.Fragment key={`line-${key}`}>
            <FlightLine
              from={from}
              to={dest}
              color={lineColor}
            />
            <Marker position={midpoint} icon={pillIcon} interactive={false} />
          </React.Fragment>
        ))}
      </MapContainer>

      {children}
    </Box>
  );
};

export default memo(InlineMapPreview);
